import React, { useState, useEffect, useRef } from 'react';
import ZonePopup from './ZonePopup';
import ProgressBar from './ProgressBar';
import './Map.css';
import BackgroundPattern from './BackgroundPattern';

function Map({ team }) {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [teamProgress, setTeamProgress] = useState({});
  const [codeWord, setCodeWord] = useState('');
  const svgRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [showCongrats, setShowCongrats] = useState(false);
  const [completionShown, setCompletionShown] = useState(false);

  useEffect(() => {
    loadSvgMap();
  }, []);

  useEffect(() => {
    if (team && team.id) {
      fetchTeamZones();
    }
  }, [team]);

  useEffect(() => {
    if (svgContent && zones.length > 0) {
      // Добавляем небольшую задержку для уверенности что DOM обновился
      const timer = setTimeout(() => {
        updateSvgZones();
        addSvgInteractivity();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [svgContent, teamProgress, zones]);

  // Отдельный useEffect для первоначальной настройки SVG
  useEffect(() => {
    if (svgContent && svgRef.current) {
      const timer = setTimeout(() => {
        updateSvgZones();
        addSvgInteractivity();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [svgContent]);

  // Показываем поздравление при завершении всех зон (один раз)
  useEffect(() => {
    const unlockedCount = Object.values(teamProgress).filter(Boolean).length;
    const totalZones = zones.length;
    if (!completionShown && totalZones > 0 && unlockedCount === totalZones) {
      setShowCongrats(true);
      setCompletionShown(true);
    }
  }, [teamProgress, zones, completionShown]);

  const loadSvgMap = async () => {
    try {
      const response = await fetch('/map.svg');
      const svgText = await response.text();
      setSvgContent(svgText);
    } catch (error) {
      console.error('Error loading SVG map:', error);
    }
  };

  const fetchTeamZones = async () => {
    try {
      const response = await fetch(`/api/teams/${team.id}/zones/`);
      if (response.ok) {
        const data = await response.json();
        setZones(data);
        
        // Создаем объект прогресса команды
        const progress = {};
        data.forEach(zone => {
          progress[zone.id] = zone.is_unlocked;
        });
        setTeamProgress(progress);
      } else {
        console.error('Failed to fetch team zones');
      }
    } catch (error) {
      console.error('Error fetching team zones:', error);
    }
  };

  const updateSvgZones = () => {
    if (!svgRef.current) return;

    const svgElement = svgRef.current.querySelector('svg');
    if (!svgElement) return;

    const lockedLayer = svgElement.querySelector('#layer1-9');
    const unlockedLayer = svgElement.querySelector('#layer1-9-1');

    if (!lockedLayer || !unlockedLayer) return;

    // ВАЖНО: Всегда показываем оба слоя сначала
    lockedLayer.style.display = 'inline';
    unlockedLayer.style.display = 'inline';
    lockedLayer.style.visibility = 'visible';
    unlockedLayer.style.visibility = 'visible';

    // Для каждой зоны в SVG определяем её состояние
    const allZonePaths = [
      ...lockedLayer.querySelectorAll('[inkscape\\:label]'),
      ...unlockedLayer.querySelectorAll('[inkscape\\:label]')
    ];

    allZonePaths.forEach(path => {
      const zoneLabel = path.getAttribute('inkscape:label');
      if (!zoneLabel || isNaN(zoneLabel)) return;

      // Ищем зону по ID (label в SVG соответствует ID зоны в БД)
      const zone = zones.find(z => z.id.toString() === zoneLabel);
      
      if (!zone) {
        // Скрываем зоны, которые не доступны команде
        path.style.display = 'none';
        return;
      }

      const isUnlocked = teamProgress[zone.id] || false;

      // Скрываем/показываем соответствующие элементы
      if (path.closest('#layer1-9')) {
        // Элемент в locked слое
        path.style.display = isUnlocked ? 'none' : 'inline';
        path.style.visibility = isUnlocked ? 'hidden' : 'visible';
      } else if (path.closest('#layer1-9-1')) {
        // Элемент в unlocked слое
        path.style.display = isUnlocked ? 'inline' : 'none';
        path.style.visibility = isUnlocked ? 'visible' : 'hidden';
      }
    });
  };

  const handleUnlockZone = async () => {
    try {
      const response = await fetch('/api/unlock/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code_word: codeWord,
          session_key: team.sessionKey,
          zone_id: selectedZone?.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Обновляем зоны
        const updatedZones = zones.map(z => 
          z.id === data.zone_id ? { ...z, is_unlocked: true, unlocked_at: new Date().toISOString() } : z
        );
        setZones(updatedZones);
        
        // Обновляем прогресс команды
        setTeamProgress(prev => ({ ...prev, [data.zone_id]: true }));
        
        setCodeWord('');
        setShowPopup(false);
        
        // Проверяем завершение
        const newUnlockedCount = Object.values({ ...teamProgress, [data.zone_id]: true }).filter(Boolean).length;
        if (!completionShown && updatedZones.length > 0 && newUnlockedCount === updatedZones.length) {
          setShowCongrats(true);
          setCompletionShown(true);
        }
        
        // Принудительно обновляем зоны после разблокировки
        setTimeout(() => {
          updateSvgZones();
          addSvgInteractivity();
        }, 100);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Ошибка при разблокировке зоны');
      }
    } catch (error) {
      console.error('Unlock error:', error);
      alert('Ошибка сети');
    }
  };

  const handleZoneClick = (zone) => {
    setSelectedZone(zone);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedZone(null);
    setCodeWord('');
    
    // Принудительно обновляем зоны после закрытия popup
    setTimeout(() => {
      updateSvgZones();
      addSvgInteractivity();
    }, 100);
  };

  const handleSvgZoneClick = (event) => {
    const path = event.target.closest('[inkscape\\:label]');
    if (!path) return;

    const zoneLabel = path.getAttribute('inkscape:label');
    if (!zoneLabel || isNaN(zoneLabel)) return;

    // Ищем зону по ID
    const zone = zones.find(z => z.id.toString() === zoneLabel);
    if (zone) {
      handleZoneClick(zone);
    }
  };

  const addSvgInteractivity = () => {
    if (!svgRef.current) return;

    const svgElement = svgRef.current.querySelector('svg');
    if (!svgElement) return;

    const allZonePaths = svgElement.querySelectorAll('[inkscape\\:label]');

    // Сначала очищаем старые обработчики
    allZonePaths.forEach(path => {
      path.removeEventListener('click', handleSvgZoneClick);
      path.style.cursor = '';
    });

    // Добавляем новые обработчики
    allZonePaths.forEach(path => {
      const zoneLabel = path.getAttribute('inkscape:label');
      if (!zoneLabel || isNaN(zoneLabel)) return;

      // Проверяем, доступна ли эта зона команде
      const zone = zones.find(z => z.id.toString() === zoneLabel);
      if (!zone) return;

      // Добавляем интерактивность только к доступным зонам
      path.style.cursor = 'pointer';
      path.addEventListener('click', handleSvgZoneClick);
      
      // Добавляем hover эффекты
      path.addEventListener('mouseenter', () => {
        path.style.opacity = '0.8';
      });
      
      path.addEventListener('mouseleave', () => {
        path.style.opacity = '';
      });
    });

    // Очистка при размонтировании
    return () => {
      allZonePaths.forEach(path => {
        path.removeEventListener('click', handleSvgZoneClick);
      });
    };
  };

  const unlockedCount = Object.values(teamProgress).filter(Boolean).length;
  const totalZones = zones.length;

  return (
    <div className="map-container">
      <BackgroundPattern />
      <div className="map-header">
        <h2>Карта маршрутов</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <ProgressBar 
            unlocked={unlockedCount} 
            total={totalZones}
          />

        </div>
      </div>

      <div className="map-content">
        <div className="svg-map-container">
          {svgContent ? (
            <div
              ref={svgRef}
              className="interactive-map"
              style={{ width: '100%', height: 'auto' }}
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          ) : (
            <div className="loading-map">Загрузка карты...</div>
          )}
        </div>

        <div className="sidebar">
          <div className="team-info">
            <h3>Команда: {team?.name}</h3>
            <div className="team-stats">
              <div className="stat-box">
                <span className="stat-number">{unlockedCount}/{totalZones}</span>
                <span className="stat-label">Зоны</span>
              </div>
            </div>
            <div className="instructions">
              <p>💡 <strong>Как играть:</strong></p>
              <p>Кликайте по зонам на карте для получения подсказок и ввода кодовых слов</p>
            </div>
          </div>
        </div>
      </div>

      {showPopup && selectedZone && (
        <ZonePopup
          zone={selectedZone}
          codeWord={codeWord}
          setCodeWord={setCodeWord}
          onUnlock={handleUnlockZone}
          onClose={handleClosePopup}
          isUnlocked={teamProgress[selectedZone.id]}
        />
      )}

      {showCongrats && (
        <div className="popup-overlay">
          <div className="zone-popup">
            <button className="close-button" onClick={() => setShowCongrats(false)}>×</button>
            <h2>Поздравляем! Вы прошли игру. Для получения награды вернитесь в лобби (СГЮА)</h2>
          </div>
        </div>
      )}
    </div>
  );
}

export default Map;